import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Flame,
  Gift,
  Layers,
  LiveDot,
  Rocket,
  Shield,
  Trophy,
} from '@/components/ui';
import type { IconComponent } from '@/components/ui';
import { useMarket } from '@/providers/market';
import { cn } from '@/lib/cn';
import { compact, eth, shortAddress, timeAgo } from '@/lib/format';
import type { ActivityKind, DemoActivity } from '@/lib/demo/types';

const FEED_LIMIT = 14;

type KindSpec = {
  verb: string;
  Icon: IconComponent;
  text: string;
  bg: string;
  border: string;
  flash: 'up' | 'down' | 'none';
};

/** One row of copy + colour per activity kind. Colour is never the only signal. */
const KINDS: Record<ActivityKind, KindSpec> = {
  buy: {
    verb: 'bought',
    Icon: ArrowUp,
    text: 'text-up',
    bg: 'bg-up/12',
    border: 'border-up/25',
    flash: 'up',
  },
  sell: {
    verb: 'sold',
    Icon: ArrowDown,
    text: 'text-down',
    bg: 'bg-down/12',
    border: 'border-down/25',
    flash: 'down',
  },
  redeem: {
    verb: 'redeemed at floor',
    Icon: Shield,
    text: 'text-floor',
    bg: 'bg-floor/12',
    border: 'border-floor/25',
    flash: 'none',
  },
  launch: {
    verb: 'launched',
    Icon: Rocket,
    text: 'text-bull',
    bg: 'bg-bull/12',
    border: 'border-bull/25',
    flash: 'up',
  },
  graduate: {
    verb: 'graduated',
    Icon: Trophy,
    text: 'text-up',
    bg: 'bg-up/12',
    border: 'border-up/25',
    flash: 'up',
  },
  'floor-lift': {
    verb: 'lifted the floor',
    Icon: Layers,
    text: 'text-floor',
    bg: 'bg-floor/12',
    border: 'border-floor/25',
    flash: 'up',
  },
  buyback: {
    verb: 'bought back',
    Icon: Flame,
    text: 'text-bull',
    bg: 'bg-bull/12',
    border: 'border-bull/25',
    flash: 'up',
  },
  claim: {
    verb: 'claimed',
    Icon: Gift,
    text: 'text-info',
    bg: 'bg-info/12',
    border: 'border-info/25',
    flash: 'none',
  },
};

function FeedRow({ entry, isNew }: { entry: DemoActivity; isNew: boolean }) {
  const spec = KINDS[entry.kind];
  const flash =
    !isNew || spec.flash === 'none'
      ? undefined
      : spec.flash === 'up'
        ? 'animate-flash-up'
        : 'animate-flash-down';

  return (
    <li className={cn('border-b border-line/60 last:border-b-0', isNew && 'animate-slide-in-right')}>
      <Link
        to={`/token/${entry.token.address}`}
        className={cn(
          'flex items-center gap-2.5 px-3 py-2 transition-colors duration-200 ease-swift hover:bg-elevated/70',
          flash,
        )}
      >
        <span
          className={cn(
            'grid h-6 w-6 shrink-0 place-items-center rounded-md border',
            spec.border,
            spec.bg,
            spec.text,
          )}
        >
          <spec.Icon size={13} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-baseline gap-1">
            <span className="truncate text-2xs text-muted">{spec.verb}</span>
            {typeof entry.ethAmount === 'number' && Number.isFinite(entry.ethAmount) ? (
              <span className={cn('shrink-0 font-mono text-2xs', spec.text)}>
                {eth(entry.ethAmount)}
              </span>
            ) : null}
          </span>
          <span className="flex min-w-0 items-baseline gap-1 text-3xs text-subtle">
            <span className="truncate font-mono uppercase text-muted">{entry.token.symbol}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate font-mono">
              {entry.actorHandle ?? shortAddress(entry.actor)}
            </span>
          </span>
        </span>

        <span className="shrink-0 font-mono text-3xs text-subtle">{timeAgo(entry.ts)}</span>
      </Link>
    </li>
  );
}

/**
 * Cross-token firehose. Rows are keyed by activity id so React only animates
 * genuinely new entries; the first paint stays still.
 */
export function LiveFeed({ className }: { className?: string }) {
  const { feed, tokens } = useMarket();

  const entries = useMemo(() => feed.slice(0, FEED_LIMIT), [feed]);
  const seen = useRef<Set<string>>(new Set());
  const [primed, setPrimed] = useState(false);
  // First paint is never "new": rows only animate once the set has been primed.
  const newSet = new Set(
    primed ? entries.filter((e) => !seen.current.has(e.id)).map((e) => e.id) : [],
  );

  useEffect(() => {
    for (const entry of entries) seen.current.add(entry.id);
    // The feed runs for as long as the tab is open — keep the ledger bounded.
    if (seen.current.size > 600) seen.current = new Set(entries.map((e) => e.id));
    if (!primed) setPrimed(true);
  }, [entries, primed]);

  const trades24h = useMemo(
    () => tokens.reduce((sum, token) => sum + (Number.isFinite(token.trades24h) ? token.trades24h : 0), 0),
    [tokens],
  );

  return (
    <aside
      className={cn(
        'hidden lg:sticky lg:top-36 lg:block lg:self-start',
        className,
      )}
      aria-label="Live activity"
    >
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <LiveDot tone="up" />
          <h2 className="font-display text-sm font-semibold text-fg">Live activity</h2>
          <span className="ml-auto text-2xs text-subtle">Across all tokens</span>
        </div>

        {entries.length === 0 ? (
          <p className="px-3 py-6 text-center text-2xs text-subtle">Waiting for the first trade…</p>
        ) : (
          <ul className="list-none" role="log" aria-live="polite" aria-relevant="additions">
            {entries.map((entry) => (
              <FeedRow key={entry.id} entry={entry} isNew={newSet.has(entry.id)} />
            ))}
          </ul>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-line bg-elevated/40 px-3 py-2">
          <span className="text-3xs uppercase tracking-[0.14em] text-subtle">Trades, 24h</span>
          <span className="font-mono text-2xs text-fg">{compact(trades24h, 0)}</span>
        </div>
      </div>
    </aside>
  );
}
